import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { acceptSurfacedProposal, dismissProposal, type Insight } from "../../lib/api";
import { qk, recapQuery } from "../../lib/queries";
import { LinkButton } from "../ui/button";
import { RecapBar, RecapOverlayCard } from "./recap";
import { InsightsSection, type InsightState } from "./insights";

type Props = {
  /**
   * The card is up. Held by the console (`DoormanArea`) rather than here,
   * because the minimised bar hangs in the masthead and the card floats over
   * the log — two places on opposite sides of a scroller, and one fact.
   */
  open: boolean;
  onMinimise: () => void;
  /**
   * Open one knock's thread. The briefing's ONLY verb since 2026-08-26: the
   * card states the case, and the answering happens in the conversation.
   */
  onOpenKnock: (knockId: string) => void;
  /**
   * The footer's "See all" — the inbox, opened on the Everything view. The only
   * navigation this screen still owns: the other rooms are in the shell now, and
   * this one is not a room but a narrowing of the list already on screen.
   */
  onSeeAll: () => void;
  onSessionExpired: () => void;
};

/**
 * The Morning Nob (M16, FR-7.3) — the primary recipient surface and the first
 * thing a signed-in door owner reads (§2.3). All the network state lives here:
 * the recap itself and the grounded insights with their linked proposals.
 *
 * WHAT IS NOT HERE ANY MORE: the per-item action state, the thirty-second undo,
 * and the ranking-feedback verbs. A recap card is a summary with one way into
 * the conversation (owner, 2026-08-26), so there is nothing on this surface to
 * write, undo or rate — `useInboxActions` belongs to the desk, which is where
 * those decisions are taken.
 */
export function RecapArea({ open, onMinimise, onOpenKnock, onSeeAll, onSessionExpired }: Props) {
  const queryClient = useQueryClient();
  const { data: recap, isError, refetch } = useQuery(recapQuery());

  const [insightState, setInsightState] = useState<Record<string, InsightState>>({});

  const expired = useRef(onSessionExpired);
  expired.current = onSessionExpired;
  const minimise = useRef(onMinimise);
  minimise.current = onMinimise;

  /**
   * READING SOMETHING ELSE FOLDS THE BRIEFING (owner, 2026-08-27).
   *
   * The card floats over the conversation and the only way out of it was the
   * one small chevron in its corner, which is a modal's manners without a
   * modal's excuse. Anywhere outside it now folds it.
   *
   * AND THE CLICK STILL LANDS. There is no scrim: the overlay's own layer is
   * `pointer-events-none` and this listens on the document, so the button
   * somebody actually reached for gets its press in the same gesture. A
   * dismisser that ate the first click would make every control on this screen
   * cost two.
   *
   * `click`, NOT `mousedown` — and the difference is a whole class of bug
   * (gauntlet walk 2026-09-13, G-3/G3). Folding on the press moved the screen
   * between the press and the release: the bar this card folds into hangs in
   * the MASTHEAD, so everything under it — the whole prompt shelf — dropped
   * 44px while the finger was still down, and a browser fires no `click` when
   * the target moves out from under the release. "Personalize your house
   * rules" and the shelf's own toggle both cost two presses, and the second
   * press worked only because by then the card was already folded. Nothing may
   * move under a finger between the press and the release, so the fold waits
   * for the click it is riding beside — which is also the event the control
   * under that finger has just been given.
   */
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent): void => {
      const target = event.target as Element | null;
      if (!target || typeof target.closest !== "function") return;
      // Inside the card is reading the briefing. On the folded bar is asking
      // for it — and the bar is only ever on screen when this is not, so that
      // clause is a guard against a future layout, not today's.
      if (target.closest("[data-recap-card]") || target.closest(".nob-recap-bar")) return;
      minimise.current();
    };
    document.addEventListener("click", onDown);
    return () => document.removeEventListener("click", onDown);
  }, [open]);

  // --- insights (FR-7.4) — accept or dismiss the linked House Rules proposal ---

  const setInsight = useCallback((id: string, next: Partial<InsightState>) => {
    setInsightState((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { settled: null, busy: false }), ...next },
    }));
  }, []);

  const acceptInsight = useCallback(
    async (insight: Insight) => {
      if (!insight.proposal_id) return;
      setInsight(insight.id, { busy: true });
      const r = await acceptSurfacedProposal(insight.proposal_id);
      if (!r.ok) {
        setInsight(insight.id, { busy: false });
        if (r.code === "UNAUTHENTICATED") expired.current();
        return;
      }
      setInsight(insight.id, { busy: false, settled: "accepted" });
      // The queue is one shorter and the document has a new version; both are
      // read elsewhere (the rules screen, the inbox's badge). The doorman's
      // thread too — accepting a rules edit answers the `edit_rules` prompt on
      // the server, and the shelf above this must stop asking for it.
      void queryClient.invalidateQueries({ queryKey: qk.proposals() });
      void queryClient.invalidateQueries({ queryKey: qk.rulesCurrent() });
      void queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
    },
    [setInsight, queryClient],
  );

  const dismissInsight = useCallback(
    async (insight: Insight) => {
      if (!insight.proposal_id) return;
      setInsight(insight.id, { busy: true });
      const r = await dismissProposal(insight.proposal_id);
      if (!r.ok) {
        setInsight(insight.id, { busy: false });
        if (r.code === "UNAUTHENTICATED") expired.current();
        return;
      }
      setInsight(insight.id, { busy: false, settled: "dismissed" });
      void queryClient.invalidateQueries({ queryKey: qk.proposals() });
    },
    [setInsight, queryClient],
  );

  // Minimised, or nothing to brief with yet. A card that floated up saying
  // "Reading your door." would put a box over the conversation to announce that
  // it has nothing to say.
  if (!open) return null;

  if (isError && !recap) {
    return (
      <div className="pointer-events-none absolute inset-0 z-20 flex justify-center px-3 pt-3 sm:px-4">
        <section
          aria-label="The Morning Nob"
          className="nob-recap-card nob-enter pointer-events-auto h-fit w-full max-w-2xl rounded-2xl px-6 py-5"
        >
          <p className="text-[15px] text-text-soft" role="alert">
            Nob is not answering. Try that again.
          </p>
          <p className="mt-4">
            <LinkButton type="button" onClick={() => void refetch()}>
              Try again
            </LinkButton>
          </p>
        </section>
      </div>
    );
  }

  if (!recap) return null;

  return (
    <RecapOverlayCard
      recap={recap}
      insights={
        <InsightsSection
          insights={recap.insights}
          state={insightState}
          onAccept={(insight) => void acceptInsight(insight)}
          onDismiss={(insight) => void dismissInsight(insight)}
        />
      }
      onSeeAll={onSeeAll}
      onMinimise={onMinimise}
      onOpenKnock={onOpenKnock}
    />
  );
}

/**
 * The minimised briefing, for the thread column's masthead slot.
 *
 * A SECOND COMPONENT RATHER THAN A PROP, because the two states of the briefing
 * live on opposite sides of a scroller: the bar above it, the card over it.
 * Both read `recapQuery()` and TanStack answers the second from the cache, so
 * this is one network read rendered in two places — not two reads that could
 * disagree about what today looks like.
 *
 * Nothing at all while the card is up, and nothing until the recap has landed:
 * an empty bar reserving its own height would push the conversation down and
 * then push it back.
 */
export function RecapBarArea({ open, onOpen }: { open: boolean; onOpen: () => void }) {
  const { data: recap } = useQuery(recapQuery());
  if (open || !recap) return null;
  return <RecapBar recap={recap} onOpen={onOpen} />;
}
